import { useEffect, useState } from 'react';
import type { CaptureItem, CaptureXPreview } from '@almanac/contracts';

type PreviewResult = { preview: CaptureXPreview | null };
type PreviewState =
  | { status: 'loading' }
  | { status: 'ready'; preview: CaptureXPreview }
  | { status: 'unavailable' };

/** Session-scoped promises prevent repeated requests and limit historical enrichment to three at a time. */
export function createCapturePreviewLoader(load: (id: string) => Promise<PreviewResult>) {
  const requests = new Map<string, Promise<PreviewResult>>();
  const queue: Array<() => void> = [];
  let active = 0;
  function drain() {
    while (active < 3 && queue.length) {
      active += 1;
      queue.shift()!();
    }
  }
  return (id: string, retry = false) => {
    if (!retry && requests.has(id)) return requests.get(id)!;
    const request = new Promise<PreviewResult>((resolve, reject) => {
      queue.push(() => {
        void load(id)
          .then(resolve, reject)
          .finally(() => {
            active -= 1;
            drain();
          });
      });
    });
    requests.set(id, request);
    drain();
    return request;
  };
}
type PreviewLoader = ReturnType<typeof createCapturePreviewLoader>;

/** Only recognized status links trigger the server's authenticated X lookup. */
function isXPost(url: string | null) {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return (
      ['https:', 'http:'].includes(parsed.protocol) &&
      /^(?:(?:www|mobile|m)\.)?(?:x\.com|twitter\.com)$/i.test(parsed.hostname) &&
      /^\/(?:[a-z\d_]+|i\/web)\/status\/\d+\/?$/i.test(parsed.pathname)
    );
  } catch {
    return false;
  }
}

function sameLink(left: string, right: string | null) {
  if (!right) return false;
  try {
    const a = new URL(left);
    const b = new URL(right);
    return a.hostname.toLowerCase() === b.hostname.toLowerCase() && a.pathname === b.pathname;
  } catch {
    return false;
  }
}

/** Remove the shared source URL from displayed notes; the outbound link remains available once. */
function noteText(item: CaptureItem) {
  return item.text
    .replace(/https?:\/\/[^\s<>]+/gi, (link) => (sameLink(link, item.url) ? '' : link))
    .trim();
}

interface CaptureContentProps {
  item: CaptureItem;
  loadPreview: PreviewLoader;
}

/** A native post card keeps shared content and the owner's accompanying note distinct. */
export function CaptureContent({ item, loadPreview }: CaptureContentProps) {
  if (isXPost(item.url)) return <XCaptureContent item={item} loadPreview={loadPreview} />;
  // Older rows stored the first line untrimmed, so compare against the trimmed title.
  const title = item.title.trim();
  const titleIsUrl = sameLink(title, item.url);
  let description = noteText(item);
  if (!titleIsUrl) {
    const rest = description.startsWith(title) ? description.slice(title.length) : null;
    if (rest === '') description = '';
    else if (rest !== null && /^[ \t]*\n/u.test(rest)) description = rest.trimStart();
  }
  return (
    <>
      {!titleIsUrl ? (
        <h3 className="break-words text-lg font-semibold text-balance sm:text-base">{title}</h3>
      ) : null}
      {description ? <NoteBody>{description}</NoteBody> : null}
      {item.url ? <SourceLink url={item.url} /> : null}
    </>
  );
}

function NoteBody({ children }: { children: string }) {
  return (
    <p className="whitespace-pre-wrap break-words text-base text-pretty text-muted-foreground sm:text-sm">
      {children}
    </p>
  );
}

function SourceLink({ url }: { url: string }) {
  return (
    <p className="break-all text-base sm:text-sm">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-primary underline underline-offset-4 hover:text-foreground"
      >
        {url}
      </a>
    </p>
  );
}

function XCaptureContent({ item, loadPreview }: CaptureContentProps) {
  const [state, setState] = useState<PreviewState>(() => {
    if (item.x_preview) return { status: 'ready', preview: item.x_preview };
    return { status: 'loading' };
  });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (item.x_preview) {
      setState({ status: 'ready', preview: item.x_preview });
      return;
    }
    let alive = true;
    setState({ status: 'loading' });
    void loadPreview(item.capture_id, attempt > 0)
      .then(({ preview }) => {
        if (!alive) return;
        if (preview) setState({ status: 'ready', preview });
        else setState({ status: 'unavailable' });
        return undefined;
      })
      .catch(() => {
        if (alive) setState({ status: 'unavailable' });
      });
    return () => {
      alive = false;
    };
  }, [item.capture_id, item.x_preview, loadPreview, attempt]);
  const note = noteText(item);
  return (
    <>
      {note ? (
        <div className="space-y-1">
          <p className="text-base font-medium sm:text-sm">Your note</p>
          <NoteBody>{note}</NoteBody>
        </div>
      ) : null}
      {state.status === 'ready' ? (
        <XPostCard preview={state.preview} />
      ) : (
        <div className="space-y-3 rounded-lg bg-muted/40 p-4 ring-1 ring-foreground/10">
          <output className="block text-base text-muted-foreground sm:text-sm">
            {state.status === 'loading' ? 'Loading X post…' : 'X preview unavailable.'}
          </output>
          <SourceLink url={item.url!} />
          {state.status === 'unavailable' ? (
            <button
              type="button"
              className="min-h-10 text-base text-primary underline underline-offset-4 hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring sm:text-sm"
              onClick={() => setAttempt((current) => current + 1)}
            >
              Retry preview
            </button>
          ) : null}
        </div>
      )}
    </>
  );
}

/** Render API text as escaped content without executing remote embeds or post markup. */
function XPostCard({ preview }: { preview: CaptureXPreview }) {
  const name = preview.author?.name || preview.author?.username || 'X post';
  return (
    <figure className="space-y-4 rounded-lg bg-muted/40 p-4 ring-1 ring-foreground/10">
      <figcaption className="flex flex-wrap items-start justify-between gap-3 text-base sm:text-sm">
        <div className="min-w-0">
          <p className="break-words font-semibold">{name}</p>
          {preview.author?.username ? (
            <p className="text-muted-foreground">@{preview.author.username}</p>
          ) : null}
        </div>
        <p className="shrink-0 text-muted-foreground">X post</p>
      </figcaption>
      <blockquote className="whitespace-pre-wrap break-words text-base text-pretty text-foreground">
        {preview.text}
      </blockquote>
      <div className="flex flex-wrap items-center justify-between gap-3 text-base sm:text-sm">
        {preview.created_at ? (
          <time dateTime={preview.created_at} className="text-muted-foreground">
            {new Date(preview.created_at).toLocaleString()}
          </time>
        ) : null}
        <a
          href={preview.url}
          target="_blank"
          rel="noreferrer"
          className="text-primary underline underline-offset-4 hover:text-foreground"
        >
          Open on X
        </a>
      </div>
    </figure>
  );
}
